import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

import { resolveMediaUrl } from '../utils/media';

// Configurateur générique piloté par `produits_local.option_schema` (contrat documenté dans
// migration-minisite-mariage.sql). Ne connaît rien de spécifique au "mini-site de mariage" :
// n'importe quel produit futur avec un option_schema non vide réutilise ce composant tel quel.
//
// Contrat attendu (voir la migration pour le détail complet) :
//   optionSchema = {
//     version: 1,
//     options: [
//       { key, type: 'choice', label, description?, required?,
//         choices: [{ value, label, price_delta_cents, preview?, font_family?, hex? }, ...] },
//       { key, type: 'toggle', label, description?, price_delta_cents },
//       ...
//     ]
//   }
//
// `onChange(selections, deltaCents)` est rappelé à chaque changement avec :
//   - selections : { [key]: valeur choisie (choice) | booléen (toggle) } — à stocker tel quel
//     dans cartes_v2.params côté commande (CheckoutForm.js).
//   - deltaCents : somme des suppléments de prix (choix "choice" + toggles actifs), à additionner
//     au prix de base du produit (même mécanique que les grattages supplémentaires existants).
const formatDelta = (cents) => `${(Number(cents) / 100).toFixed(2).replace('.', ',')} €`;

const ProductOptionConfigurator = ({ optionSchema, onChange }) => {
  const options = useMemo(
    () => (Array.isArray(optionSchema?.options) ? optionSchema.options.filter(Boolean) : []),
    [optionSchema]
  );

  const [selections, setSelections] = useState(() => {
    const initial = {};
    options.forEach((opt) => {
      if (opt.type === 'choice') {
        initial[opt.key] = opt.choices?.[0]?.value ?? null;
      } else if (opt.type === 'toggle') {
        initial[opt.key] = false;
      }
    });
    return initial;
  });

  // Recalcule le supplément total à chaque changement et le remonte au parent (ProductDetail.js)
  // pour l'affichage du prix total et la construction de l'objet envoyé au panier.
  useEffect(() => {
    let deltaCents = 0;
    options.forEach((opt) => {
      if (opt.type === 'choice') {
        const choice = opt.choices?.find((c) => c.value === selections[opt.key]);
        deltaCents += Number(choice?.price_delta_cents) || 0;
      } else if (opt.type === 'toggle' && selections[opt.key]) {
        deltaCents += Number(opt.price_delta_cents) || 0;
      }
    });
    onChange?.(selections, deltaCents);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections, options]);

  const setValue = (key, value) => setSelections((prev) => ({ ...prev, [key]: value }));

  if (options.length === 0) return null;

  return (
    <Box>
      {options.map((opt) => {
        if (opt.type === 'choice') {
          return (
            <Box key={opt.key} sx={{ marginTop: 3 }}>
              <Typography variant="h6">{opt.label}</Typography>
              {opt.description && (
                <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
                  {opt.description}
                </Typography>
              )}
              <Grid container spacing={1.5} sx={{ marginTop: 0.5 }}>
                {(opt.choices || []).map((choice) => {
                  const active = selections[opt.key] === choice.value;
                  return (
                    <Grid item xs={4} sm={3} md={2} key={choice.value}>
                      <Paper
                        elevation={active ? 4 : 1}
                        onClick={() => setValue(opt.key, choice.value)}
                        sx={{
                          cursor: 'pointer',
                          border: active ? '2px solid #c48f8b' : '2px solid transparent',
                          padding: 1,
                          textAlign: 'center',
                          height: '100%',
                        }}
                      >
                        {choice.preview && (
                          <img
                            src={resolveMediaUrl(choice.preview)}
                            alt={choice.label}
                            style={{ width: '100%', borderRadius: 4, display: 'block' }}
                            onError={(e) => { e.target.style.visibility = 'hidden'; }}
                          />
                        )}
                        {choice.hex && !choice.preview && (
                          <Box
                            sx={{
                              width: '60%',
                              paddingTop: '60%',
                              margin: '0 auto',
                              borderRadius: '50%',
                              backgroundColor: choice.hex,
                              border: '1px solid rgba(0,0,0,0.1)',
                            }}
                          />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            marginTop: 0.5,
                            fontFamily: choice.font_family || undefined,
                            fontSize: choice.font_family ? '1.15rem' : undefined,
                          }}
                        >
                          {choice.label}
                        </Typography>
                        {!!choice.price_delta_cents && (
                          <Typography variant="caption" color="secondary" sx={{ display: 'block' }}>
                            +{formatDelta(choice.price_delta_cents)}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          );
        }

        if (opt.type === 'toggle') {
          return (
            <Box sx={{ marginTop: 3 }} key={opt.key}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!selections[opt.key]}
                    onChange={(e) => setValue(opt.key, e.target.checked)}
                    color="secondary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {opt.label}
                      {!!opt.price_delta_cents && (
                        <Typography component="span" variant="body2" color="secondary" sx={{ marginLeft: 1 }}>
                          (+{formatDelta(opt.price_delta_cents)})
                        </Typography>
                      )}
                    </Typography>
                    {opt.description && (
                      <Typography variant="body2" color="text.secondary">
                        {opt.description}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </Box>
          );
        }

        return null;
      })}
    </Box>
  );
};

export default ProductOptionConfigurator;
